import { BusyWatcher, Router, watchBusy } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Modules } from "config";
import { action, computed, observable } from "mobx";
import EventBus, { totalCountCardGateways } from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import NotificationService from "services/notificationService";
import emptyListResponse from "utils/emptyListResponse";
import { isFilterEmpty } from "utils/filters";
import CardGatewayDetailViewModel from "./paymentCardGatewayDetailViewModel";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import ConfirmationService from "services/confirmationService";
import OptionsService from "services/optionsService";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import ListViewModel from "viewModels/listViewModel";
import SearchPayGatePaymentsRequest, { SearchPayGatePaymentsRequestValidationRules } from "entities/searchPayGatePaymentsRequest";
import type CardGatewayPayment from "entities/cardGatewayPaymentResponse";
import PaymentsRepository from "repositories/paymentsRepository";
import EnumService from "services/enumService";
import CardGatewayPaymentClassificationResponse from "entities/cardGatewayPaymentClassificationResponse";
import { resetDirty } from "@frui.ts/dirtycheck";
import TerminalType from "entities/terminalType";
import CardNumberSearchRequest from "entities/cardNumberSearchRequest";

const navigationName = "card-gateway";

@Router.registerRoute({ name: Router.Self, route: navigationName, children: [CardGatewayDetailViewModel] })
export default class PaymentCardGatewayViewModel extends ListViewModel<
  CardGatewayPayment,
  CardGatewayDetailViewModel,
  ScreenBase,
  SearchPayGatePaymentsRequest
> {
  private unsubscribtions: Array<() => void> = [];
  readonly modelName = "paymentCardGateway";
  @observable busyWatcher = new BusyWatcher();
  @observable totalCountCardGateways: number;
  countrySelectViewModel: CountrySelectViewModel;
  protected abortController?: AbortController;

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: PaymentsRepository,
    public detailViewModelFactory: ReturnType<typeof CardGatewayDetailViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    protected enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
    this.initFilter();
  }

  onInitialize() {
    super.onInitialize();
  }

  protected onActivate() {
    this.eventBus.publish(totalCountCardGateways({ totalCountCardGateways: this.totalCountCardGateways ?? 0 }));
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscribtions.forEach(unsubsribe => unsubsribe());
      this.unsubscribtions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }

  @computed
  get name() {
    return this.translate("cardGateway");
  }

  @computed
  get isFilterEmpty() {
    return isFilterEmpty(this.appliedFilter);
  }

  @computed
  get terminalTypes() {
    return this.optionsService.internalEnumOptions(TerminalType);
  }

  @action.bound
  allowItemDetail(firstPart: any) {
    return false;
  }

  @action.bound
  onTotalCountCardGateways(count: number) {
    this.eventBus.publish(totalCountCardGateways({ totalCountCardGateways: count ?? 0 }));
  }

  protected createFilter() {
    return attachAutomaticValidator(new SearchPayGatePaymentsRequest(), SearchPayGatePaymentsRequestValidationRules);
  }

  applyFilter() {
    if (!isFilterEmpty(this.filter) && !validate(this.filter)) {
      return false;
    }
    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  @watchBusy
  protected async fetchData() {
    if (this.isFilterEmpty) {
      return emptyListResponse();
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    this.abortController = new AbortController();

    const requestData = this.appliedFilter;
    if (this.appliedFilter._cardNumberFirst || this.appliedFilter._cardNumberSecond) {
      requestData.cardNumber = new CardNumberSearchRequest();
      if (requestData.cardNumber && this.appliedFilter._cardNumberFirst) {
        requestData.cardNumber.firstPart = this.appliedFilter._cardNumberFirst;
      }
      if (requestData.cardNumber && this.appliedFilter._cardNumberSecond) {
        requestData.cardNumber.secondPart = this.appliedFilter._cardNumberSecond;
      }
    }

    const data = await this.repository.getAllCardGatewayPayments(
      this.pagingFilter,
      this.appliedFilter,
      this.abortController.signal
    );
    this.abortController = undefined;
    this.totalCountCardGateways = data.metadata.total;

    this.eventBus.publish(totalCountCardGateways({ totalCountCardGateways: data.metadata.total }));

    return data;
  }

  @watchBusy
  protected resetFilterValues(filter: SearchPayGatePaymentsRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    if (this.eventBus) {
      this.totalCountCardGateways = 0;
      this.onTotalCountCardGateways(0);
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.${code}`, size);
  }

  translateFilter(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.filter.${code}`, size);
  }

  translatePlaceholder(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.placeholder.${code}`, size);
  }

  @action.bound
  translatePaymentTemplate(paymentClassification: CardGatewayPaymentClassificationResponse) {
    if (paymentClassification?.isReversal) {
      return this.translate("reversal");
    }
    if (paymentClassification?.paymentTemplateId) {
      const paymentTemplate = this.enumService
        .values("payment_templates")
        ?.find(paymentTemplate => paymentTemplate.id === paymentClassification.paymentTemplateId);

      if (!paymentTemplate) {
        return;
      }

      return paymentTemplate.messageTemplate;
    }
  }
}
